// LawnTrack Feature Flags - Gate features by subscription tier

// Tier hierarchy (higher number = more features)
const TIER_LEVELS = {
  free: 0,
  basic: 1,
  pro: 2,
  premium: 3,
};

// Feature to minimum tier mapping
const FEATURE_TIERS = {
  // Pro tier features
  expense_tracking: 'pro',
  reports: 'pro',
  square_integration: 'pro',

  // Premium tier features
  customer_portal: 'premium',
  multi_user: 'premium',
  advanced_reports: 'premium',

  // Basic/Free features (everyone gets these)
  customers: 'free',
  routes: 'free',
  storm_mode: 'free',
  weather: 'free',
  intake_form: 'free',
  discounts: 'free',
  multiple_properties: 'free',
};

// Human-readable feature names for UI
const FEATURE_NAMES = {
  expense_tracking: 'Expense Tracking',
  reports: 'Profit & Loss Reports',
  square_integration: 'Square Integration',
  customer_portal: 'Customer Portal',
  multi_user: 'Multi-User Access',
  advanced_reports: 'Advanced Reports',
  customers: 'Customer Management',
  routes: 'Route Management',
  storm_mode: 'Service Mode',
  weather: 'Weather Forecasts',
  intake_form: 'Customer Intake Form',
  discounts: 'Referral Discounts',
  multiple_properties: 'Multiple Properties',
};

// Human-readable tier names
const TIER_NAMES = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
  premium: 'Premium',
};

/**
 * Check if a user's tier has access to a feature
 * @param {string} userTier - The user's subscription tier (free, basic, pro, premium)
 * @param {string} featureKey - The feature key to check
 * @returns {boolean} - True if user has access, false otherwise
 */
export const hasFeature = (userTier, featureKey) => {
  const requiredTier = FEATURE_TIERS[featureKey];

  // If feature doesn't exist in our map, deny access by default
  if (!requiredTier) {
    console.warn(`Unknown feature: ${featureKey}`);
    return false;
  }

  const userLevel = TIER_LEVELS[userTier] ?? 0;
  const requiredLevel = TIER_LEVELS[requiredTier] ?? 0;

  return userLevel >= requiredLevel;
};

/**
 * Get the required tier for a feature
 * @param {string} featureKey - The feature key
 * @returns {string} - The tier name required for this feature
 */
export const getRequiredTier = (featureKey) => {
  return FEATURE_TIERS[featureKey] || 'premium';
};

/**
 * Get the human-readable name of a feature
 * @param {string} featureKey - The feature key
 * @returns {string} - Human-readable feature name
 */
export const getFeatureName = (featureKey) => {
  return FEATURE_NAMES[featureKey] || featureKey;
};

/**
 * Get the human-readable name of a tier
 * @param {string} tierKey - The tier key
 * @returns {string} - Human-readable tier name
 */
export const getTierName = (tierKey) => {
  return TIER_NAMES[tierKey] || tierKey;
};

/**
 * Get all features available at a specific tier
 * @param {string} tierKey - The tier to check
 * @returns {string[]} - Array of feature keys available at this tier
 */
export const getFeaturesForTier = (tierKey) => {
  const tierLevel = TIER_LEVELS[tierKey] ?? 0;

  return Object.entries(FEATURE_TIERS)
    .filter(([, requiredTier]) => TIER_LEVELS[requiredTier] <= tierLevel)
    .map(([feature]) => feature);
};

export { FEATURE_TIERS, TIER_LEVELS, FEATURE_NAMES, TIER_NAMES };
